import {adminClient} from 'helper/httpClient';
import {
    useSWRPaginationTemplate,
    useSWRTemplate,
} from 'hooks/dataHooksTemplate';
import {cleanObjectNilValue} from 'utils/clean';
import {get, isNil, keyBy, keys, some, split} from 'lodash';
import {sleep_} from '@genx/july/lib/commonjs/lang';
import {useCallback, useMemo, useState} from 'react';
import {allPermissions} from 'constant/permissions';
import {processUserProfile} from 'data/userProfile';
import { useAuth } from 'hooks/useAuth';

export const useAllPermissions = () => {
    const swr = useSWRTemplate({
        key: ['allSystemPermissions'],
        request: async ([_]) => {
            await sleep_(1000);
            return allPermissions;
        },
        defaultValue: [],
    });
    return swr;
};

const processSub = (item) => {
    if (item?.children) {
        return {
            ...item,
            children: keyBy(item.children.map(processSub), item => item.code.split('/').pop()),
        };
    } else {
        return item;
    }
};

export const useAdminPermissionSettings = (options = {}) => {
    const {roles = []} = options;
    const {isUser} = useAuth()
    const swr = useSWRTemplate({
        key: roles?.length && !isUser ? ['permissionSettings']: null,
        request: async ([_]) => {
            // await sleep_(1000);
            let data = {}
            for (const key of roles) {
                const {result} = await adminClient.get(['permissions'], { role: key })
                data[key] = result
            }
            return data
        },
        defaultValue: {},
        // config: {
        //     revalidateOnFocus: false,
        //     revalidateOnReconnect: false,
        //     refreshInterval:0
        // },
    })

    const searchOptPermission = useMemo(
        () =>
            keys(get(swr, 'data', {})).reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr]: keyBy(
                        get(swr, `data.${curr}`, []).map(processSub),
                        'code',
                    ),
                };
            }, {}),
        [swr.data],
    );
    return {
        ...swr,
        getPermission: useCallback(
            (key, type = 'read') => {
                return some(
                    roles.map((role) => {
                        const setting = get(searchOptPermission, role);
                        if (isNil(setting)) {
                            return false;
                        }
                        const splitKeys = split(key, '.');
                        const permission = get(
                            setting,
                            `${splitKeys.join('.children.')}.${type}`,
                            false,
                        );
                        return permission;
                    }),
                );
            },
            [searchOptPermission],
        ),
    };
};

export const useSystemRoles = () => {
    const swr = useSWRTemplate({
        key: ['adminSystemRoles'],
        request: async ([_]) => {
            const {result} = await adminClient.get(['role-type']);
            return result.filter((item) => !['DEVELOPER', 'IT_ADMIN'].includes(item.code));
        },
        defaultValue: [],
    });
    return swr;
};

export const useSystemRolesAccess = (role) => {
    const swr = useSWRTemplate({
        key: role ? ['adminSystemRolesAccess', role] : null,
        request: async ([_, _role]) => {
            const {result} = await adminClient.get(['permissions', _role]);
            return result;
        },
        defaultValue: [],
    });
    return swr;
};

export const useUpdatePermission = () => {
    const [isLoading, setIsLoading] = useState(false);

    const trigger = useCallback(async ({code, write, read, role}) => {
        setIsLoading(true);
        try {
            const {result} = await adminClient.put(['permissions', code], {
                write,
                read,
                role,
            });
            return result;
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {trigger, isLoading};
};

export const useCreatePermission = () => {
    const [isLoading, setIsLoading] = useState(false);

    const trigger = useCallback(async ({write, read, role, parent, permission}) => {
        setIsLoading(true);
        try {
            const {result} = await adminClient.post(['permissions'], {
                write,
                read,
                role,
                parent,
                permission,
            });
            return result;
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {trigger, isLoading};
};

export const useAdminSystemUsers = (options = {}) => {
    const {username} = options;
    const swr = useSWRPaginationTemplate({
        key: ['adminSysmteUsers', username],
        request: async (_key, _pageIndex, _pageSize) => {
            const [_, _username] = _key;
            const {result} = await adminClient.get(['staff'], {
                username: _username,
                pageIndex: _pageIndex - 1,
                pageSize: _pageSize,
            });
            return {
                result: {
                    totalItems: get(result, 'totalItems', 0),
                    items: get(result, 'items', []).map(processUserProfile),
                },
            };
        },
        defaultValue: [],
    });
    return swr;
};
